import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./BlogPost.css";

import {
  getBlogs,
  getBlogById,
  updateBlog,
  deleteBlog,
  getImages,
} from "../services/blogService";

const emptyForm = {
  title: "",
  content: "",
  status: "DRAFT",
  meta_title: "",
  meta_description: "",
  featured_image: [],
};

const PAGE_SIZE = 10;

const isVideoUrl = (url = "") => /\.(mp4|webm|ogg)(?:$|\?)/i.test(url);

const getVideoMimeType = (url = "") => {
  if (/\.webm(?:$|\?)/i.test(url)) return "video/webm";
  if (/\.ogg(?:$|\?)/i.test(url)) return "video/ogg";
  return "video/mp4";
};

const toImageList = (post) => {
  const raw = post?.featured_image ?? post?.featuredImage ?? [];
  if (Array.isArray(raw)) {
    return raw.filter(Boolean);
  }
  return raw ? [raw] : [];
};

const normalizeBlog = (post) => ({
  ...post,
  id: post?.id ?? post?._id ?? "",
  title: post?.title || "",
  content: post?.content || post?.description || "",
  meta_title: post?.meta_title || post?.metaTitle || "",
  meta_description: post?.meta_description || post?.metaDescription || "",
  featured_image: toImageList(post),
  status: post?.status || "DRAFT",
});

const BlogPost = () => {
  const adminBasePath = "/components/Admins/AdminLayout";
  const navigate = useNavigate();

  const [posts, setPosts] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);
  const [availableImages, setAvailableImages] = useState([]);
  const [selectedDatabaseImages, setSelectedDatabaseImages] = useState([]);
  const [showImageDropdown, setShowImageDropdown] = useState(false);

  const fetchBlogs = useCallback(async (page) => {
    try {
      const res = await getBlogs({ page, limit: PAGE_SIZE });

      console.log("Blogs API Response:", res);

      const payload = res?.data ?? res ?? {};
      const blogs =
        payload?.blogs ||
        payload?.content ||
        payload?.data?.blogs ||
        payload?.data ||
        [];

      setPosts(Array.isArray(blogs) ? blogs.map(normalizeBlog) : []);
      setTotalPages(Math.max(1, payload?.totalPages || 1));
      setTotalPosts(payload?.total || (Array.isArray(blogs) ? blogs.length : 0));
    } catch (err) {
      console.error("Fetch blogs failed:", err);
      setPosts([]);
      setTotalPages(1);
      setTotalPosts(0);
    }
  }, []);

  useEffect(() => {
    fetchBlogs(currentPage);
  }, [currentPage, fetchBlogs]);

  const summaryLabel = useMemo(() => {
    const publishedCount = posts.filter(
      (post) => post.status === "PUBLISHED"
    ).length;

    return `${totalPosts} posts | ${publishedCount} published`;
  }, [posts, totalPosts]);

  const resetForm = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setSelectedDatabaseImages([]);
    setShowImageDropdown(false);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const fetchImages = async () => {
    try {
      const res = await getImages();

      let images = [];

      if (Array.isArray(res)) {
        images = res;
      } else if (Array.isArray(res?.data)) {
        images = res.data;
      }

      const formattedImages = images
        .map((img, index) => ({
          id: img.id || img._id || index,
          name: img.name || img.filename || `Image ${index + 1}`,
          url:
            img.url ||
            img.fileUrl ||
            img.imageUrl ||
            img.secure_url ||
            img.src ||
            "",
        }))
        .filter((image) => image.url);

      setAvailableImages(formattedImages);
      setShowImageDropdown(true);
    } catch (err) {
      console.error("Fetch images failed:", err);
    }
  };

  const handleDatabaseImageSelect = (event) => {
    const values = Array.from(
      event.target.selectedOptions,
      (option) => option.value
    );
    setSelectedDatabaseImages(values);
  };

  const handleAddSelectedImages = () => {
    if (selectedDatabaseImages.length === 0) return;

    setFormData((prev) => ({
      ...prev,
      featured_image: [
        ...prev.featured_image,
        ...selectedDatabaseImages.filter(
          (url) => !prev.featured_image.includes(url)
        ),
      ],
    }));

    setSelectedDatabaseImages([]);
    setShowImageDropdown(false);
  };

  const removeSelectedImage = (indexToRemove) => {
    setFormData((prev) => ({
      ...prev,
      featured_image: prev.featured_image.filter(
        (_, index) => index !== indexToRemove
      ),
    }));
  };

  const handleEdit = async (post) => {
    const fallbackPost = normalizeBlog(post);
    setSelectedDatabaseImages([]);
    setShowImageDropdown(false);

    try {
      const response = await getBlogById(fallbackPost.id);
      const payload = response?.data ?? response;
      const resolvedPost =
        payload?.blog ?? payload?.data ?? payload ?? fallbackPost;
      const normalizedPost = normalizeBlog(resolvedPost);

      setEditingId(normalizedPost.id || fallbackPost.id);
      setFormData({
        title: normalizedPost.title,
        content: normalizedPost.content,
        meta_title: normalizedPost.meta_title,
        meta_description: normalizedPost.meta_description,
        featured_image: normalizedPost.featured_image,
        status: normalizedPost.status,
      });
    } catch (err) {
      console.error("Fetch blog details failed:", err);

      setEditingId(fallbackPost.id);
      setFormData({
        title: fallbackPost.title,
        content: fallbackPost.content,
        meta_title: fallbackPost.meta_title,
        meta_description: fallbackPost.meta_description,
        featured_image: fallbackPost.featured_image,
        status: fallbackPost.status,
      });
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleUpdate = async () => {
    if (!editingId) return;

    if (!formData.title.trim() || !formData.content.trim()) {
      alert("Title and Content are required");
      return;
    }

    try {
      const payload = {
        title: formData.title.trim(),
        content: formData.content.trim(),
        meta_title: formData.meta_title || "",
        meta_description: formData.meta_description || "",
        status: formData.status || "DRAFT",
        featured_image: formData.featured_image || [],
      };

      console.log("Updating blog:", payload);

      await updateBlog(editingId, payload);

      await fetchBlogs(currentPage);
      resetForm();
    } catch (err) {
      console.error("Update failed", err.response?.data || err.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteBlog(id);

      const nextPage = posts.length === 1 && currentPage > 1
        ? currentPage - 1
        : currentPage;

      if (nextPage !== currentPage) {
        setCurrentPage(nextPage);
      } else {
        await fetchBlogs(currentPage);
      }

      if (editingId === id) resetForm();
    } catch (err) {
      console.error("Delete failed", err);
    }
  };

  return (
    <div className="blog-post-page">
      <section className="blog-post-hero">
        <div>
          <h1>All Blog Posts</h1>
          <p>Posts created in Blog Management are listed here.</p>
          <span className="blog-post-summary">{summaryLabel}</span>
        </div>

        <button
          className="blog-post-back-btn"
          onClick={() => navigate(`${adminBasePath}/blog-management`)}
        >
          Back To Blog Management
        </button>
      </section>

      {editingId && (
        <section className="blog-post-edit-card">
          <div className="blog-post-edit-head">
            <h2>Edit Blog Post</h2>
            <button className="blog-post-cancel-btn" onClick={resetForm}>
              Cancel
            </button>
          </div>

          <div className="blog-post-edit-grid">
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Post title"
            />

            <textarea
              name="content"
              value={formData.content}
              onChange={handleInputChange}
              placeholder="Post content"
            />

            <input
              type="text"
              name="meta_title"
              value={formData.meta_title}
              onChange={handleInputChange}
              placeholder="SEO title"
            />

            <input
              type="text"
              name="meta_description"
              value={formData.meta_description}
              onChange={handleInputChange}
              placeholder="Meta description"
            />

            <select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
            >
              <option value="DRAFT">DRAFT</option>
              <option value="PUBLISHED">PUBLISHED</option>
            </select>

            <div className="blog-post-featured-field">
              <label className="blog-post-featured-label">Featured Image</label>

              <div className="blog-post-image-actions">
                <button
                  type="button"
                  className="blog-post-inline-btn"
                  onClick={() => navigate(`${adminBasePath}/admin-images`)}
                >
                  Upload
                </button>

                <button
                  type="button"
                  className="blog-post-inline-btn"
                  onClick={fetchImages}
                >
                  Choose Image
                </button>

                <button
                  type="button"
                  className="blog-post-inline-btn"
                  onClick={handleAddSelectedImages}
                >
                  Add
                </button>
              </div>

              {showImageDropdown && (
                <select
                  className="blog-post-image-select"
                  value={selectedDatabaseImages}
                  onChange={handleDatabaseImageSelect}
                  multiple
                >
                  {availableImages.map((image) => (
                    <option key={image.id} value={image.url}>
                      {image.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {formData.featured_image.length > 0 && (
            <div className="blog-post-image-grid">
              {formData.featured_image.map((url, index) => (
                <div key={`${url}-${index}`} className="blog-post-edit-image-tile">
                  {isVideoUrl(url) ? (
                    <video
                      className="blog-post-edit-video"
                      controls
                      preload="metadata"
                    >
                      <source src={url} type={getVideoMimeType(url)} />
                    </video>
                  ) : (
                    <img
                      className="blog-post-edit-image"
                      src={url}
                      alt="Blog preview"
                    />
                  )}
                  <button
                    type="button"
                    className="blog-post-remove-image-btn"
                    onClick={() => removeSelectedImage(index)}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="blog-post-edit-actions">
            <button className="blog-post-update-btn" onClick={handleUpdate}>
              Update Post
            </button>
          </div>
        </section>
      )}

      <section className="blog-post-list-wrap">
        {posts.length === 0 && (
          <div className="blog-post-empty">
            No posts found. Create a post first.
          </div>
        )}

        {posts.map((post) => (
          <article key={post.id} className="blog-post-card">
            <div className="blog-post-top">
              <h2>{post.title || "Untitled Post"}</h2>

              <span
                className={`blog-post-status ${
                  post.status === "PUBLISHED" ? "live" : "draft"
                }`}
              >
                {post.status === "PUBLISHED" ? "Published" : "Draft"}
              </span>
            </div>

            <p className="blog-post-content">
              {post.content || "No content available."}
            </p>

            <p className="blog-post-meta">
              SEO Title: {post.meta_title || "Not set"}
            </p>

            <p className="blog-post-meta">
              Meta Description: {post.meta_description || "Not set"}
            </p>

            {post.featured_image?.length > 0 && (
              <div className="blog-post-image-grid">
                {post.featured_image.map((url, index) => (
                  isVideoUrl(url) ? (
                    <video
                      key={`${post.id}-${index}`}
                      className="blog-post-video"
                      controls
                      preload="metadata"
                    >
                      <source src={url} type={getVideoMimeType(url)} />
                    </video>
                  ) : (
                    <img
                      key={`${post.id}-${index}`}
                      className="blog-post-image"
                      src={url}
                      alt={post.title}
                    />
                  )
                ))}
              </div>
            )}

            <div className="blog-post-actions">
              <button onClick={() => handleEdit(post)}>Edit</button>

              <button
                className="danger"
                onClick={() => handleDelete(post.id)}
              >
                Delete
              </button>
            </div>
          </article>
        ))}

        {totalPosts > 0 && (
          <div className="blog-post-pagination">
            <button
              type="button"
              className="blog-post-page-btn"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>

            <span className="blog-post-page-indicator">
              Page {currentPage} of {totalPages}
            </span>

            <button
              type="button"
              className="blog-post-page-btn"
              onClick={() =>
                setCurrentPage((prev) => Math.min(totalPages, prev + 1))
              }
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default BlogPost;
